import React, { useState, useMemo } from 'react';
import useProductsData from '../hooks/useProductsData';
import { slugify, optimizeImage, generateSrcSet } from '../utils';
import { Link } from 'react-router-dom';
import { Download, Search, FileText, ChevronRight, Home } from 'lucide-react';
import SEO from '../components/SEO';
import Schema from '../components/Schema';
import { SITE_URL } from '../data/Constants';


const ResourcesPage = () => {
   const { products: productsData, loading: dataLoading } = useProductsData();
   const [searchQuery, setSearchQuery] = useState("");

   // Helper to extract all resources from a product
   const extractResources = (product) => {
      const resources = [];
      const types = ['spec sheet', 'user manual', 'installation manual', 'brochure', 'CAD drawing', 'manual'];

      types.forEach(type => {
         const items = product[type];
         if (!items) return;
         const list = Array.isArray(items) ? items : [items];

         list.forEach(url => {
            if (typeof url === 'string' && url.length > 0) {
               let name = type.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '); // Capitalize
               // Try to get filename
               try {
                  const filename = decodeURIComponent(url.split('/').pop().split('?')[0]);
                  name = `${name}: ${filename.replace(/%20/g, ' ').replace(/^\w+-\d+-/, '')}`;
               } catch (e) { }

               resources.push({
                  type: type,
                  url,
                  name,
                  displayType: type.charAt(0).toUpperCase() + type.slice(1)
               });
            }
         });
      });
      return resources;
   };

   const resourcesData = useMemo(() => {
      const data = [];
      productsData.forEach(product => {
         const productResources = extractResources(product);
         if (productResources.length > 0) {
            data.push({
               productName: product.name,
               productSlug: slugify(product.name), // Ensure slug is generated correctly
               brand: product.company,
               resources: productResources
            });
         }
      });
      return data;
   }, [productsData]);

   const filteredData = useMemo(() => {
      if (!searchQuery) return resourcesData;
      const q = searchQuery.toLowerCase();

      return resourcesData.filter(item => {
         const matchesProduct = item.productName.toLowerCase().includes(q) || item.brand.toLowerCase().includes(q);
         // Also check if any resource name matches? Maybe overkill
         return matchesProduct;
      });
   }, [resourcesData, searchQuery]);


   return (
      <div className="min-h-screen pt-[72px] bg-white animate-fade-in">
         <SEO
            title="Fireplace Manuals, Brochures & Spec Sheets"
            description="Download official installation manuals, specification sheets, CAD drawings, and brochures for our premium gas, electric, and outdoor fireplaces."
            canonical={`${SITE_URL}/resources`}
         />
         <Schema
            type="BreadcrumbList"
            data={{
               items: [
                  { name: 'Home', path: '/' },
                  { name: 'Resources', path: '/resources' }
               ]
            }}
         />
         <Schema
            type="CollectionPage"
            data={{
               name: "Fireplace Manuals, Brochures & Spec Sheets",
               description: "Download official installation manuals, specification sheets, CAD drawings, and brochures for Mainland Fireplaces' gas, electric, and outdoor fireplaces.",
               url: `${SITE_URL}/resources`
            }}
         />
         <Schema
            type="FAQPage"
            data={{
               questions: [
                  {
                     question: "Where can I find the installation manual for my fireplace?",
                     answer: "You can find installation manuals, specification sheets, and brochures for Mainland Fireplaces by searching for your product or brand on our Resources page."
                  },
                  {
                     question: "What types of resources are available for download?",
                     answer: "We provide spec sheets, user manuals, installation manuals, CAD drawings, and brochures for our premium gas and electric fireplace collection of top brands across Canada."
                  }
               ]
            }}
         />
         <div className="bg-secondary text-white py-16 md:py-24 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
               <img 
                  src={optimizeImage('https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&auto=format&fit=crop', 1200)}
                  srcSet={generateSrcSet('https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&auto=format&fit=crop')}
                  sizes="100vw"
                  fetchpriority="high"
                  alt="Resources Background"
                  className="w-full h-full object-cover"
               />
            </div>
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
               <h1 className="font-heading text-4xl md:text-5xl uppercase mb-4">Downloads & Resources</h1>
               <p className="text-gray-200 max-w-2xl mx-auto mb-4">Access specification sheets, installation manuals, brochures, and CAD drawings for our premium fireplace collection.</p>
               <p className="text-gray-300 max-w-3xl mx-auto text-sm">Whether you are looking for fireplace installation manuals, product brochures, detailed specification sheets, or CAD drawings, our comprehensive resource center has everything you need. Find official documentation for our full range of gas fireplaces, electric inserts, and outdoor fire features to ensure safe installation and operation.</p>
            </div>
         </div>

         <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto mb-12">
               <input
                  type="text"
                  placeholder="Search for a product or brand..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-full py-4 pl-12 pr-6 text-sm placeholder:text-gray-500 focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-sm transition-all"
               />
               <Search className="absolute left-4 top-4 w-5 h-5 text-gray-500" />
            </div>

            {dataLoading ? (
               <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4"></div>
                  <p>Loading resources...</p>
               </div>
            ) : filteredData.length === 0 ? (
               <div className="text-center py-20 text-gray-600">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No resources found matching your search.</p>
               </div>
            ) : (
               <div className="grid grid-cols-1 gap-8">
                  {filteredData.map((item, idx) => (
                     <div key={idx} className="bg-white border border-gray-100 rounded-sm hover:shadow-md transition-shadow duration-300 p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 pb-4 border-b border-gray-100 gap-4">
                           <div>
                              <span className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1 block">{item.brand}</span>
                              <h2 className="font-heading text-xl text-secondary uppercase">
                                 <Link to={`/products/${item.productSlug}`} className="hover:text-primary transition-colors flex items-center gap-2 group">
                                    {item.productName}
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0" />
                                 </Link>
                              </h2>
                           </div>
                           <Link to={`/products/${item.productSlug}`} className="text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-primary transition-colors">
                              View Product Details
                           </Link>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                           {item.resources.map((res, rIdx) => (
                              <a
                                 key={rIdx}
                                 href={res.url}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 hover:border-primary/50 hover:bg-white rounded-sm transition-all group"
                              >
                                 <div className="w-8 h-8 rounded-full bg-white border border-gray-200 group-hover:border-primary group-hover:bg-primary/5 flex items-center justify-center shrink-0 transition-colors">
                                    <Download className="w-4 h-4 text-gray-500 group-hover:text-primary" />
                                 </div>
                                 <div className="flex flex-col overflow-hidden min-w-0">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600 truncate">{res.displayType}</span>
                                    <span className="text-[10px] text-gray-500 truncate" title={res.name}>{res.name.split(':')[1] || res.name}</span>
                                 </div>
                              </a>
                           ))}
                        </div>
                     </div>
                  ))}
               </div>
            )}
         </div>
      </div>
   );
};

export default ResourcesPage;
